import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  FormControlLabel,
  Paper
} from '@mui/material';
import {
  Description as DocumentIcon,
  Psychology as AIIcon,
  CheckCircleOutline as SuccessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

import { apiClient } from '@/services/api';

// 日志类型标签映射
const getLogTypeLabel = (type: string) => {
  const types: { [key: string]: string } = {
    'writing': '主日志',
    'general': '通用',
    'overview': '概览',
    'fund': '资金',
    'process': '过程',
    'benefit': '效益',
  };
  return types[type] || type;
};

interface ProjectInfo {
  id: string;
  project_name: string;
  industry_type: string;
  client_level: string;
  evaluation_type: string;
  description: string;
}

interface DocumentInfo {
  id: number;
  title: string;
  log_type: string;
  content: string;
  log_date?: string;
  source?: string;
  created_at?: string;
  updated_at?: string;
}

interface AIIndicatorRecommendDialogProps {
  open: boolean;
  onClose: () => void;
  project: ProjectInfo;
  onRecommendationComplete: (result: any) => void;
}

export default function AIIndicatorRecommendDialog({
  open,
  onClose,
  project,
  onRecommendationComplete
}: AIIndicatorRecommendDialogProps) {
  // 状态管理
  const [step, setStep] = useState(0); // 0: 选择模板, 1: 选择文档, 2: 推荐中, 3: 显示结果
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<any>(null);
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([]);

  // 当对话框打开时，重置状态
  useEffect(() => {
    if (!open) {
      // 重置状态
      console.log('对话框关闭，重置所有状态到初始状态');
      setStep(0);
      setSelectedTemplate(null);
      setDocuments([]);
      setSelectedDocuments([]);
      setLoading(false);
      setError(null);
      setRecommendation(null);
      setSelectedIndicators([]);
    } else {
      console.log('对话框打开');
    }
  }, [open, project]);

  // 监听全局文档刷新事件
  useEffect(() => {
    const handleGlobalRefresh = () => {
      console.log('Global document refresh event received in AIIndicatorRecommendDialog');
      // 只有在对话框打开时才刷新文档列表
      if (open) {
        fetchDocuments();
      }
    };

    window.addEventListener('documentsShouldRefresh', handleGlobalRefresh);

    return () => {
      window.removeEventListener('documentsShouldRefresh', handleGlobalRefresh);
    };
  }, [open, project]);

  // 获取项目文档列表（从本地文件系统）
  const fetchDocuments = async () => {
    setError(null);
    
    try {
      console.log('开始获取文档列表...');
      const responseData = await apiClient.get(`/projects/${project.id}/indicators/ai/documents`);
      console.log('ApiClient返回数据:', responseData);
      console.log('responseData.success:', responseData?.success);
      console.log('responseData类型:', typeof responseData);
      
      // ApiClient已经解包了一层，responseData就是后端的响应体
      if (!responseData) {
        throw new Error('API响应为空');
      }
      
      // 检查success字段
      if (responseData.success !== true) {
        console.log('success字段值:', responseData.success);
        console.log('success字段类型:', typeof responseData.success);
        throw new Error(`API响应success为false或不存在: ${responseData.success}`);
      }
      
      // 检查data字段
      if (!responseData.data || !responseData.data.documents) {
        console.log('data字段:', responseData.data);
        throw new Error('API响应中缺少data.documents字段');
      }
      
      const documents = responseData.data.documents;
      console.log('文档列表:', documents);
      
      // 过滤出有内容的日志
      const textDocs = documents.filter((doc: DocumentInfo) => 
        doc.content && doc.content.trim().length > 0
      );
      
      setDocuments(textDocs);
      
      if (textDocs.length === 0) {
        throw new Error('暂无项目日志内容，请先添加或提取项目日志后再使用AI推荐功能');
      }
      
      console.log(`获取到 ${textDocs.length} 个文档`);
      
    } catch (err: any) {
      console.error('获取文档列表失败:', err);
      console.error('错误详情:', err.response?.data);
      throw new Error(`文档数据获取错误: ${err.message || '网络错误'}`);
    }
  };

  // 处理日志选择
  const handleDocumentToggle = (document: DocumentInfo) => {
    setSelectedDocuments(prev => {
      const isSelected = prev.some(doc => doc.id === document.id);
      if (isSelected) {
        return prev.filter(doc => doc.id !== document.id);
      } else {
        return [...prev, document];
      }
    });
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments([...documents]);
    }
  };

  // 开始推荐指标
  const handleStartRecommendation = async () => {
    if (selectedDocuments.length === 0) {
      setError('请至少选择一个文档');
      return;
    }
    
    setStep(2);
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/projects/${project.id}/indicators/ai/recommend`, {
        project_info: {
          project_name: project.project_name,
          industry_type: selectedTemplate,
          client_level: project.client_level,
          evaluation_type: project.evaluation_type,
          description: project.description
        },
        selected_documents: selectedDocuments.map(doc => doc.id.toString()),
        user_requirement: ''
      }, { timeout: 180000 }); // 3分钟超时
      
      console.log('AI推荐API响应:', response);
      
      if (response && response.success) {
        console.log('推荐成功，设置推荐结果');
        setRecommendation(response);
        // 自动选中所有推荐的指标
        const allIndicatorNames = response.data.recommendedIndicators.map((indicator: any) => indicator.indicator_name);
        setSelectedIndicators(allIndicatorNames);
        console.log('设置步骤为3（显示结果页面）');
        setStep(3);
        console.log(`成功推荐了 ${response.data.totalIndicatorCount} 个指标，当前步骤: 3`);
      } else {
        throw new Error(response?.error || '推荐结果格式错误');
      }
    } catch (err: any) {
      console.error('推荐指标失败:', err);
      setError(err.message);
      setStep(1); // 回到选择文档步骤
    } finally {
      setLoading(false);
    }
  };

  // 处理指标选择
  const handleIndicatorSelection = (indicatorName: string, checked: boolean) => {
    if (checked) {
      setSelectedIndicators(prev => [...prev, indicatorName]);
    } else {
      setSelectedIndicators(prev => prev.filter(name => name !== indicatorName));
    }
  };

  // 处理全选
  const handleSelectAllIndicators = (checked: boolean) => {
    if (checked && recommendation) {
      const allIndicatorNames = recommendation.data.recommendedIndicators.map((indicator: any) => indicator.indicator_name);
      setSelectedIndicators(allIndicatorNames);
    } else {
      setSelectedIndicators([]);
    }
  };

  // 应用推荐结果
  const handleApplyRecommendation = () => {
    console.log('开始应用推荐结果，选中指标数量:', selectedIndicators.length);
    
    if (recommendation && selectedIndicators.length > 0) {
      // 只传递选中的指标
      const selectedIndicatorsList = recommendation.data.recommendedIndicators.filter((indicator: any) => 
        selectedIndicators.includes(indicator.indicator_name)
      );
      
      console.log('过滤后的指标数量:', selectedIndicatorsList.length);
      
      const modifiedRecommendation = {
        ...recommendation,
        data: {
          ...recommendation.data,
          recommendedIndicators: selectedIndicatorsList,
          totalIndicatorCount: selectedIndicatorsList.length
        }
      };
      
      console.log('调用 onRecommendationComplete');
      onRecommendationComplete(modifiedRecommendation);
      
      // 延迟关闭对话框，确保处理完成
      setTimeout(() => {
        console.log('延迟关闭对话框');
        onClose();
      }, 500);
    } else {
      console.warn('没有推荐结果或未选择指标');
      onClose();
    }
  };

  // 处理模板选择
  const handleTemplateSelect = async (templateType: string) => {
    setSelectedTemplate(templateType);
    setLoading(true);
    setError(null);
    try {
      setStep(1); // 进入选择文档步骤
      await fetchDocuments(); // 等待获取文档列表完成
    } catch (err: any) {
      console.error('处理模板选择失败:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 渲染步骤内容
  const renderStepContent = () => {
    switch (step) {
      case 0: // 选择模板
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              选择共性指标模板
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              请选择一个共性指标模板，系统将基于该模板的决策(A)和过程(B)指标，结合AI推荐的产出(C)和效益(D)指标，形成完整的指标体系。
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => handleTemplateSelect('重点项目绩效评价')}
                sx={{ flex: 1, height: 120, flexDirection: 'column', gap: 1 }}
              >
                <Typography variant="h6">重点项目绩效评价</Typography>
                <Typography variant="body2" color="text.secondary">
                  适用于各类重点项目的绩效评价
                </Typography>
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => handleTemplateSelect('专项债类')}
                sx={{ flex: 1, height: 120, flexDirection: 'column', gap: 1 }}
              >
                <Typography variant="h6">专项债类</Typography>
                <Typography variant="body2" color="text.secondary">
                  适用于专项债项目建设期的绩效评价
                </Typography>
              </Button>
            </Box>
          </Box>
        );

      case 1: // 选择文档
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              选择用于推荐指标的项目日志
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              请选择包含项目信息、业务流程、预期成果等内容的日志，AI将基于这些日志推荐合适的指标体系。
            </Typography>
            
            {documents.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={handleSelectAll}
                >
                  {selectedDocuments.length === documents.length ? '取消全选' : '全选'}
                </Button>
                <Typography variant="caption" sx={{ ml: 2 }}>
                  已选择 {selectedDocuments.length} / {documents.length} 个文档
                </Typography>
              </Box>
            )}
            
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {documents.map((document) => (
                <ListItem 
                  key={document.id}
                  button
                  onClick={() => handleDocumentToggle(document)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedDocuments.some(doc => doc.id === document.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemIcon>
                    <DocumentIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={document.title}
                    secondary={
                      <Box>
                        <Typography variant="caption" component="div">
                          类型: {getLogTypeLabel(document.log_type)}
                        </Typography>
                        <Typography variant="caption" component="div">
                          来源: {document.source || '手动创建'}
                        </Typography>
                        <Typography variant="caption" component="div">
                          创建时间: {document.created_at ? new Date(document.created_at).toLocaleDateString() : '未知'}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          内容预览: {document.content?.substring(0, 50)}...
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );
        
      case 2: // 推荐中
        return (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <AIIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              AI正在分析文档内容
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              基于 {selectedDocuments.length} 个日志推荐指标体系...
            </Typography>
            <LinearProgress sx={{ width: '100%', mt: 2 }} />
          </Box>
        );
        
      case 3: // 显示结果
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SuccessIcon sx={{ color: 'success.main', mr: 1 }} />
              <Typography variant="h6">
                AI推荐指标 ({recommendation?.data?.recommendedIndicators?.length || 0}个) - 请选择需要创建的指标
              </Typography>
            </Box>
            
            {recommendation && recommendation.data && (
              <Box>
                {/* 全选控制 */}
                <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedIndicators.length === recommendation.data.recommendedIndicators.length}
                        indeterminate={selectedIndicators.length > 0 && selectedIndicators.length < recommendation.data.recommendedIndicators.length}
                        onChange={(e) => handleSelectAllIndicators(e.target.checked)}
                      />
                    }
                    label={`全选 (已选择 ${selectedIndicators.length} / ${recommendation.data.recommendedIndicators.length})`}
                  />
                </Box>

                {/* 指标列表 (分组) */}
                <Stack spacing={2}>
                  {Object.entries(
                    recommendation.data.recommendedIndicators.reduce((acc: any, indicator: any) => {
                      const { level1_name, level2_name } = indicator;
                      if (!acc[level1_name]) acc[level1_name] = {};
                      if (!acc[level1_name][level2_name]) acc[level1_name][level2_name] = [];
                      acc[level1_name][level2_name].push(indicator);
                      return acc;
                    }, {})
                  ).map(([level1_name, level2_groups]: [string, any]) => (
                    <Accordion key={level1_name} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">
                          {level1_name}
                          <Chip 
                            label={`共 ${Object.values(level2_groups).flat().length} 个`} 
                            size="small" 
                            color="primary" 
                            sx={{ ml: 2 }} 
                          />
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={1.5}>
                          {Object.entries(level2_groups).map(([level2_name, indicators]: [string, any]) => (
                            <Paper key={level2_name} variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                {level2_name}
                                <Chip 
                                  label={`共 ${indicators.length} 个`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ ml: 1.5 }}
                                />
                              </Typography>
                              <Stack spacing={1}>
                                {indicators.map((indicator: any, index: number) => (
                                  <Paper key={index} elevation={0} sx={{ p: 1.5, bgcolor: 'action.selected' }}>
                                    <Box display="flex" alignItems="flex-start">
                                      <Checkbox
                                        checked={selectedIndicators.includes(indicator.indicator_name)}
                                        onChange={(e) => handleIndicatorSelection(indicator.indicator_name, e.target.checked)}
                                        sx={{ mt: -0.5 }}
                                      />
                                      <Box flex={1} ml={1}>
                                        <Typography variant="subtitle2">
                                          {indicator.indicator_name}
                                        </Typography>
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                          <Chip 
                                            label={`权重: ${indicator.weight || 'N/A'}`} 
                                            size="small" 
                                            color="primary" 
                                          />
                                          <Chip 
                                            label={indicator.evaluation_rule_type || '要素符合度计分'} 
                                            size="small" 
                                            color="secondary" 
                                          />
                                          <Chip 
                                            label={indicator.recommendation_source || 'AI推荐'} 
                                            size="small" 
                                            color="info" 
                                          />
                                        </Stack>
                                        {indicator.evaluation_rules && (
                                          <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                              评价规则:
                                            </Typography>
                                            <pre style={{ 
                                              whiteSpace: 'pre-wrap', 
                                              wordBreak: 'break-all', 
                                              margin: 0, 
                                              fontSize: '0.8rem',
                                              color: '#666',
                                              maxHeight: '120px',
                                              overflow: 'auto',
                                              backgroundColor: '#f5f5f5',
                                              padding: '8px',
                                              borderRadius: '4px',
                                              border: '1px solid #e0e0e0'
                                            }}>
                                              {indicator.evaluation_rules}
                                            </pre>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  </Paper>
                                ))}
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        );
        
      default:
        return null;
    }
  };

  // 渲染操作按钮
  const renderActions = () => {
    switch (step) {
      case 1: // 选择文档
        return (
          <>
            <Button onClick={onClose}>
              取消
            </Button>
            <Button 
              variant="contained" 
              onClick={handleStartRecommendation}
              disabled={selectedDocuments.length === 0}
            >
              开始推荐指标
            </Button>
          </>
        );
        
      case 2: // 推荐中
        return (
          <Button onClick={onClose} disabled={loading}>
            取消
          </Button>
        );
        
      case 3: // 显示结果
        return (
          <>
            <Button onClick={onClose}>
              关闭
            </Button>
            <Button 
              variant="contained" 
              onClick={handleApplyRecommendation}
              disabled={selectedIndicators.length === 0}
            >
              应用选中指标 ({selectedIndicators.length})
            </Button>
          </>
        );
        
      default:
        return (
          <Button onClick={onClose}>
            关闭
          </Button>
        );
    }
  };

  return (
    <Dialog 
      open={open} 
      // 锁定：禁止 ESC 与点击遮罩关闭；仅通过底部按钮主动关闭
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        if (loading || step === 2) return; // 推荐处理中也不允许关闭
        onClose();
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      disableEscapeKeyDown
      keepMounted
    >
      <DialogTitle>
        根据项目资料推荐指标
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {renderStepContent()}
      </DialogContent>
      
      <DialogActions>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
}